/** Periodi VP analitike. Granice su lokalna ponoć početnog dana i zadnja sekunda krajnjeg
 *  dana, u unix sekundama, jer server poredi s `confirmed_at`. */

export type AnalyticsPeriod = "7d" | "30d" | "90d" | "year" | "custom";

export const ANALYTICS_PERIODS: { value: AnalyticsPeriod; label: string }[] = [
  { value: "7d", label: "7 dana" },
  { value: "30d", label: "30 dana" },
  { value: "90d", label: "90 dana" },
  { value: "year", label: "Ova godina" },
  { value: "custom", label: "Prilagođeno" },
];

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const endOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59);
const sec = (d: Date) => Math.floor(d.getTime() / 1000);

/** "YYYY-MM-DD" iz DatePickera kao lokalni datum (`new Date("2026-10-01")` bi bio UTC). */
function parseDay(s: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
}

/** Period → granice; prilagođeni period bez oba datuma pada na 30 dana. Obrnuti datumi se
 *  zamijene, da korisnik ne dobije grešku za klik redom „do" pa „od". */
export function analyticsRange(
  period: AnalyticsPeriod,
  custom: { from: string; to: string },
  now: Date = new Date(),
): { from: number; to: number } {
  const today = startOfDay(now);
  const back = (days: number) => new Date(today.getFullYear(), today.getMonth(), today.getDate() - days);
  switch (period) {
    case "7d": return { from: sec(back(6)), to: sec(endOfDay(now)) };
    case "90d": return { from: sec(back(89)), to: sec(endOfDay(now)) };
    case "year": return { from: sec(new Date(now.getFullYear(), 0, 1)), to: sec(endOfDay(now)) };
    case "custom": {
      const a = parseDay(custom.from), b = parseDay(custom.to);
      if (a && b) {
        const [lo, hi] = a <= b ? [a, b] : [b, a];
        return { from: sec(lo), to: sec(endOfDay(hi)) };
      }
      return { from: sec(back(29)), to: sec(endOfDay(now)) };
    }
    default: return { from: sec(back(29)), to: sec(endOfDay(now)) };
  }
}

/** "1.10.–29.10.2026" ili "15.12.2025.–14.1.2026" kad period prelazi godinu. */
export function fmtRange(from: number, to: number): string {
  const a = new Date(from * 1000), b = new Date(to * 1000);
  const tail = `${b.getDate()}.${b.getMonth() + 1}.${b.getFullYear()}`;
  if (a.getFullYear() !== b.getFullYear()) return `${a.getDate()}.${a.getMonth() + 1}.${a.getFullYear()}.–${tail}`;
  if (a.getMonth() === b.getMonth() && a.getDate() === b.getDate()) return tail;
  return `${a.getDate()}.${a.getMonth() + 1}.–${tail}`;
}

const MONTHS = ["jan", "feb", "mar", "apr", "maj", "jun", "jul", "aug", "sep", "okt", "nov", "dec"];

/** Oznaka korpe trenda na osi: dan „3.10.", sedmica „od 3.10.", mjesec „okt 2026". */
export function fmtBucket(start: number, granularity: "day" | "week" | "month", long = false): string {
  const d = new Date(start * 1000);
  if (granularity === "month") return `${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
  const day = `${d.getDate()}.${d.getMonth() + 1}.`;
  if (granularity === "week") return long ? `sedmica od ${day}` : day;
  return day;
}
